import hasDistinctOptionLabels from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/constraint/hasDistinctOptionLabels';
import hasOptionsForChoice from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/FormData/constraint/hasOptionsForChoice';
import isChoiceNature from '@UI/Pages/Administration/SubjectTypes/SubjectTypeEdit/Helpers/isChoiceNature';
import SubjectFieldDraftView, {
	type SubjectFieldDraftOption,
} from '@UI/Store/SubjectTypeDefinitions/Views/SubjectFieldDraftView';
import { AbstractValidatedClass, Joi, ValidatedClass, constraint } from '@verclam/form-validator';

type FieldDraftFormArgs = {
	key: string | null;
	label: string;
	nature: string;
	natureLabel: string;
	options: readonly SubjectFieldDraftOption[];
	required: boolean;
	sentToAi: boolean;
	archived: boolean;
	/** True when dossiers of the edited type exist. */
	isUsed: boolean;
};

/**
 * Form state of one sheet field in its sidebar. It only holds the field being edited: "Apply" turns
 * it back into a `SubjectFieldDraftView` for the type draft. On a used type, the nature of a saved
 * field and its saved options are locked.
 */
@ValidatedClass()
class FieldDraftFormData extends AbstractValidatedClass<FieldDraftFormArgs> {
	public readonly key: string | null;

	@constraint(Joi.string().max(120), () => '120 caractères au plus.')
	@constraint(Joi.string().pattern(/\S/).required(), () => 'Le libellé du champ est requis.')
	public readonly label: string;

	public readonly nature: string;
	public readonly natureLabel: string;

	@constraint(
		hasDistinctOptionLabels,
		() => 'Les libellés des options doivent être renseignés et différents.',
	)
	@constraint(hasOptionsForChoice, () => 'Un champ à choix demande au moins une option.')
	public readonly options: readonly SubjectFieldDraftOption[];

	public readonly required: boolean;
	public readonly sentToAi: boolean;
	public readonly archived: boolean;
	public readonly isUsed: boolean;

	constructor(data: FieldDraftFormArgs) {
		super(data);
		this.key = data.key;
		this.label = data.label;
		this.nature = data.nature;
		this.natureLabel = data.natureLabel;
		this.options = Object.freeze(data.options.map((option) => Object.freeze({ ...option })));
		this.required = data.required;
		this.sentToAi = data.sentToAi;
		this.archived = data.archived;
		this.isUsed = data.isUsed;
		Object.freeze(this);
	}

	/** A blank field of the first served nature. */
	static createEmpty(isUsed: boolean, nature: string, natureLabel: string): FieldDraftFormData {
		return FieldDraftFormData.fromView(new SubjectFieldDraftView({ nature, natureLabel }), isUsed);
	}

	static fromView(view: SubjectFieldDraftView, isUsed: boolean): FieldDraftFormData {
		return new FieldDraftFormData({
			key: view.key,
			label: view.label,
			nature: view.nature,
			natureLabel: view.natureLabel,
			options: view.options,
			required: view.required,
			sentToAi: view.sentToAi,
			archived: view.archived,
			isUsed,
		});
	}

	/** The field as the type draft stores it. */
	toView(): SubjectFieldDraftView {
		return new SubjectFieldDraftView({
			key: this.key,
			label: this.label.trim(),
			nature: this.nature,
			natureLabel: this.natureLabel,
			options: this.options.map((option) => ({ value: option.value, label: option.label.trim() })),
			required: this.required,
			sentToAi: this.sentToAi,
			archived: this.archived,
		});
	}

	get isNatureLocked(): boolean {
		return this.isUsed && this.key !== null;
	}

	get isChoice(): boolean {
		return isChoiceNature(this.nature);
	}

	get canBeRequired(): boolean {
		return this.nature !== 'boolean';
	}

	/** A saved option of a used type cannot be removed. */
	isOptionLocked(index: number): boolean {
		const option = this.options[index];

		return this.isUsed && option !== undefined && option.value !== null;
	}

	setLabel(label: string): this {
		return this.cloneWith({ label } as Partial<FieldDraftFormArgs>);
	}

	/** A nature without choices drops the options; a yes/no field is never required. */
	setNature(nature: string, natureLabel: string): this {
		if (this.isNatureLocked) {
			return this;
		}

		return this.cloneWith({
			nature,
			natureLabel,
			options: isChoiceNature(nature) ? this.options : [],
			required: nature === 'boolean' ? false : this.required,
		} as Partial<FieldDraftFormArgs>);
	}

	/** Appends a new option; a blank label is ignored. */
	addOption(label: string): this {
		if (label.trim().length === 0) {
			return this;
		}

		return this.cloneWith({
			options: [...this.options, { value: null, label }],
		} as Partial<FieldDraftFormArgs>);
	}

	removeOption(index: number): this {
		if (this.isOptionLocked(index)) {
			return this;
		}

		return this.cloneWith({
			options: this.options.filter((_option, position) => position !== index),
		} as Partial<FieldDraftFormArgs>);
	}

	setRequired(required: boolean): this {
		return this.cloneWith({ required } as Partial<FieldDraftFormArgs>);
	}

	setSentToAi(sentToAi: boolean): this {
		return this.cloneWith({ sentToAi } as Partial<FieldDraftFormArgs>);
	}
}

export default FieldDraftFormData;
